import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, InfoIcon, LoaderIcon } from 'lucide-react';

/**
 * Shared visual language for the multi-step match funnel, so the program
 * selection start page and the questionnaire that follows it read as one flow.
 */

// Brand accent, used for progress, required-field markers, and focus rings.
export const ACCENT = '#1E7BD6';
// Primary submit action. The funnel's submit button uses the design system's
// green accent, so its hover state stays in the same family instead of
// reverting to the retired amber.
export const CTA = '#5CB335';
export const CTA_DARK = '#4A9A2A';
export interface StageInfo {
  label: string;
  steps: number[];
}

/**
 * The funnel's stages. Step 1 is program selection on the start page; steps
 * 2-7 are the questionnaire. Keeping one list here means the progress bar
 * never disagrees with itself between the two pages.
 */
export const MATCH_STAGES: StageInfo[] = [{
  label: 'YOUR PROGRAM',
  steps: [1]
}, {
  label: 'ABOUT YOU',
  steps: [2, 3, 4, 5]
}, {
  label: 'CONTACT INFO',
  steps: [6]
}, {
  label: 'YOUR SCHOOL MATCHES',
  steps: []
}];
export const MATCH_TOTAL_STEPS = 6;
export function StageProgress({
  currentStep
}: {
  currentStep: number;
}) {
  let activeStageIdx = 0;
  MATCH_STAGES.forEach((stage, idx) => {
    if (stage.steps.includes(currentStep)) activeStageIdx = idx;
  });

  // Fill tracks overall step progress so the bar always moves forward.
  const fillPercent = Math.min(100, Math.max(0, (currentStep - 1) / (MATCH_TOTAL_STEPS - 1) * 100));
  return <div className="w-full max-w-2xl mx-auto pt-8 pb-2" data-id="element-254" data-mp="3w0oiio53w92m">
      {/* Stage labels */}
      <div className="grid grid-cols-4 gap-1 mb-3" data-id="element-255" data-mp="3r1le95wya6vp">
        {MATCH_STAGES.map((s, idx) => <div key={s.label} className={`text-center text-[10px] sm:text-[11px] font-bold tracking-widest uppercase leading-tight ${idx === activeStageIdx ? '' : 'text-gray-400'}`} style={idx === activeStageIdx ? {
        color: ACCENT
      } : undefined} data-id="element-256" data-mp="3m1yxwnltsaio" data-mpi={idx}>
            {s.label}
          </div>)}
      </div>

      {/* Progress bar with dots */}
      <div className="relative" data-id="element-257" data-mp="2yevd3mem0i8s">
        <div className="absolute top-1/2 left-0 right-0 h-1 -translate-y-1/2 bg-gray-200 rounded-full" data-id="element-258" data-mp="2yfn7wv0duby0" />
        <motion.div className="absolute top-1/2 left-0 h-1 -translate-y-1/2 rounded-full" style={{
        backgroundColor: ACCENT
      }} initial={false} animate={{
        width: `${fillPercent}%`
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-259" data-mp="33dyjba6bsuic" />
        <div className="relative flex justify-between items-center" data-id="element-260" data-mp="3mb96vxilhzhy">
          {MATCH_STAGES.map((s, idx) => {
          const isActive = idx <= activeStageIdx;
          return <div key={s.label} className={`w-5 h-5 rounded-full border-2 transition-colors ${isActive ? '' : 'bg-white border-gray-300'}`} style={isActive ? {
            backgroundColor: ACCENT,
            borderColor: ACCENT
          } : undefined} data-id="element-261" data-mp="infvdpj6y74c" data-mpi={idx} />;
        })}
        </div>
      </div>

      <p className="text-center text-sm text-gray-500 mt-4" data-id="element-262" data-mp="92vlishyzafq">
        Step {currentStep} of {MATCH_TOTAL_STEPS}
      </p>
    </div>;
}
export function InfoBanner({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="mt-5 flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4" data-id="element-263" data-mp="j1l4yppil2y3">
      <div className="flex-shrink-0 mt-0.5 text-blue-600" data-id="element-264" data-mp="e1yqkcxy9gnp">
        <InfoIcon className="w-4 h-4" data-id="element-265" data-mp="e4kwy993tq4c" />
      </div>
      <p className="text-xs sm:text-sm text-blue-700 leading-relaxed" data-id="element-266" data-mp="1uf6uibzad6k5">
        {children}
      </p>
    </div>;
}
interface StepCardProps {
  title: string;
  subtitle?: string;
  onContinue: () => void;
  ctaLabel: string;
  ctaIcon?: React.ReactNode;
  info?: React.ReactNode;
  error?: string;
  isSubmitting?: boolean;
  children: React.ReactNode;
}
export function StepCard({
  title,
  subtitle,
  onContinue,
  ctaLabel,
  ctaIcon,
  info,
  error,
  isSubmitting,
  children
}: StepCardProps) {
  return (
    // A real <form> with a real submit button. Jornaya and TrustedForm both
    // certify a form element and the interactions inside it; a div full of
    // inputs with a type="button" reads as non-authentic.
    <form onSubmit={e => {
      e.preventDefault();
      onContinue();
    }} noValidate className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-10" data-id="element-267" data-mp="mwe6s8cls46p">
      {/* Certification fields. The scripts write into these by id/name, so they
          are left uncontrolled and must live inside the form element. */}
      <input type="hidden" id="leadid_token" name="leadid_token" data-id="element-268" data-mp="15nylc6fwic14" />
      <input type="hidden" id="xxTrustedFormCertUrl" name="xxTrustedFormCertUrl" data-id="element-269" data-mp="15mdtiokz176y" />

      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center leading-tight mb-2" data-id="element-270" data-mp="2l7xu9n76vpk2">
        {title}
      </h1>
      {subtitle && <p className="text-center text-sm text-gray-500 mb-6" data-id="element-271" data-mp="2h6mc22qna6lw">{subtitle}</p>}
      <div className={subtitle ? 'mt-2' : 'mt-6'} data-id="element-272" data-mp="2c81bg27hdkwx">{children}</div>

      {error && <p className="mt-3 text-sm text-red-600 text-center" data-id="element-273" data-mp="38xifyduq90x1">{error}</p>}

      <button type="submit" disabled={isSubmitting} className="mt-6 w-full inline-flex items-center justify-center h-[52px] px-8 text-white font-semibold rounded-full text-base leading-none shadow-ds-md transition-colors duration-150 ease-out disabled:opacity-50 disabled:pointer-events-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" style={{
        backgroundColor: CTA,
        backgroundImage: "none"
      }} onMouseEnter={e => {
        if (!isSubmitting) (e.currentTarget as HTMLButtonElement).style.backgroundColor = CTA_DARK;
      }} onMouseLeave={e => {
        ;
        (e.currentTarget as HTMLButtonElement).style.backgroundColor = CTA;
      }} data-id="element-274" data-mp="34vx856x1vctp">
        {isSubmitting ? <span className="inline-flex items-center gap-2" data-id="element-275" data-mp="1t25qv6cpduf">
            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-276" data-mp="1sto6pza9kcw" />
            {ctaLabel}
          </span> : <>
            {ctaLabel}
            {ctaIcon ? ctaIcon : <ArrowRightIcon className="w-5 h-5 ml-2 inline-block" data-id="element-277" data-mp="3nzqwapmx3cgn" />}
          </>}
      </button>

      {info && <InfoBanner data-id="element-278" data-mp="3szwi3p6ia4qt">{info}</InfoBanner>}
    </form>
  );
}
interface SelectFieldProps {
  value: string;
  onChange: (v: string) => void;
  options: {
    label: string;
    value: string;
  }[];
  id?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}
export function SelectField({
  value,
  onChange,
  options,
  id,
  label,
  placeholder = '-- Please Select --',
  disabled
}: SelectFieldProps) {
  return <div data-id="element-279" data-mp="1h8j384sk6t38">
      {label && <label htmlFor={id} className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-280" data-mp="1h7q15h4jorrf">
          {label}{' '}
          <span style={{
        color: ACCENT
      }} data-id="element-281" data-mp="2xqt4g2rqxpt4">
            *
          </span>
        </label>}
      <select id={id} value={value} onChange={e => onChange(e.target.value)} disabled={disabled} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed" style={{
      '--tw-ring-color': ACCENT,
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E\")",
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 0.75rem center',
      backgroundSize: '1.25rem',
      paddingRight: '2.5rem'
    } as React.CSSProperties} data-id="element-282" data-mp="3biza2ux9weeb">
        <option value="" data-id="element-283" data-mp="1j024ge68hguu">{placeholder}</option>
        {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-284" data-mp="1j253tota0mc8">
            {opt.label}
          </option>)}
      </select>
    </div>;
}